import React, { useMemo, useState } from 'react';
import ChatMarkdown from './ChatMarkdown';
import { tableToCsv } from '../utils/mcpFormat';

/**
 * SapResult
 * =========
 * Rich, read-only presentation of a parsed MCP result (see utils/mcpFormat).
 * Instead of flattening SAP payloads to Markdown, each data shape gets a proper
 * UI: searchable/sortable tables with sticky headers and CSV export, key/value
 * grids, collapsible JSON trees, and lists. No LLM, no network — pure rendering.
 */

// ---- helpers --------------------------------------------------------------
const fmtCell = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

const download = (filename, text, mime = 'text/csv') => {
  const blob = new Blob([text], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---- Table block ----------------------------------------------------------
const TableBlock = ({ block }) => {
  const { columns, rows } = block;
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ col: null, dir: 1 });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let data = rows;
    if (q) {
      data = rows.filter((r) =>
        columns.some((c) => fmtCell(r[c]).toLowerCase().includes(q))
      );
    }
    if (sort.col != null) {
      const col = sort.col;
      data = [...data].sort((a, b) => {
        const av = a[col];
        const bv = b[col];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * sort.dir;
        if (bv == null) return 1 * sort.dir;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sort.dir;
        return String(av).localeCompare(String(bv)) * sort.dir;
      });
    }
    return data;
  }, [rows, columns, query, sort]);

  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, dir: -s.dir } : { col, dir: 1 }));

  return (
    <div className="sapres-table-block">
      <div className="sapres-table-toolbar">
        <input
          className="sapres-search"
          type="text"
          placeholder="Filter rows…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="sapres-count">
          {filtered.length === rows.length
            ? `${rows.length} row${rows.length === 1 ? '' : 's'}`
            : `${filtered.length} of ${rows.length}`}
        </span>
        <button
          type="button"
          className="sapres-mini-btn"
          onClick={() => download('sap-result.csv', tableToCsv(block))}
          title="Download all rows as CSV"
        >
          ⬇ CSV
        </button>
      </div>
      <div className="sapres-table-scroll">
        <table className="sapres-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c} onClick={() => toggleSort(c)} className="sapres-th">
                  <span>{c}</span>
                  {sort.col === c && <span className="sapres-sort">{sort.dir === 1 ? ' ▲' : ' ▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, ri) => (
              <tr key={ri}>
                {columns.map((c) => (
                  <td key={c} title={fmtCell(r[c])}>{fmtCell(r[c])}</td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td className="sapres-norows" colSpan={columns.length}>No matching rows.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---- Key / value block ----------------------------------------------------
const KeyValueBlock = ({ block }) => (
  <dl className="sapres-kv">
    {block.pairs.map(([k, v]) => (
      <div className="sapres-kv-row" key={k}>
        <dt>{k}</dt>
        <dd title={fmtCell(v)}>{fmtCell(v) || <span className="sapres-muted">—</span>}</dd>
      </div>
    ))}
  </dl>
);

// ---- List block -----------------------------------------------------------
const ListBlock = ({ block }) => (
  <ul className="sapres-list">
    {block.items.map((it, i) => (
      <li key={i}>{fmtCell(it)}</li>
    ))}
  </ul>
);

// ---- JSON tree block ------------------------------------------------------
const JsonNode = ({ k, value, depth }) => {
  const [open, setOpen] = useState(depth < 1);
  const isObj = value && typeof value === 'object';

  if (!isObj) {
    return (
      <div className="sapres-json-leaf" style={{ paddingLeft: depth * 14 }}>
        {k != null && <span className="sapres-json-key">{k}: </span>}
        <span className={`sapres-json-val sapres-json-${value === null ? 'null' : typeof value}`}>
          {value === null ? 'null' : typeof value === 'string' ? `"${value}"` : String(value)}
        </span>
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((v, i) => [i, v])
    : Object.entries(value);
  const label = Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`;

  return (
    <div className="sapres-json-node">
      <div
        className="sapres-json-toggle"
        style={{ paddingLeft: depth * 14 }}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="sapres-json-caret">{open ? '▾' : '▸'}</span>
        {k != null && <span className="sapres-json-key">{k} </span>}
        <span className="sapres-json-muted">{label}</span>
      </div>
      {open &&
        entries.map(([ck, cv]) => (
          <JsonNode key={ck} k={ck} value={cv} depth={depth + 1} />
        ))}
    </div>
  );
};

const JsonBlock = ({ block }) => {
  const [raw, setRaw] = useState(false);
  return (
    <div className="sapres-json-block">
      <div className="sapres-json-toolbar">
        <button type="button" className="sapres-mini-btn" onClick={() => setRaw((r) => !r)}>
          {raw ? 'Tree view' : 'Raw JSON'}
        </button>
      </div>
      {raw ? (
        <pre className="sapres-raw"><code>{JSON.stringify(block.value, null, 2)}</code></pre>
      ) : (
        <div className="sapres-json-tree"><JsonNode value={block.value} depth={0} /></div>
      )}
    </div>
  );
};

// ---- Dispatcher -----------------------------------------------------------
const Block = ({ block }) => {
  switch (block.kind) {
    case 'table':
      return <TableBlock block={block} />;
    case 'keyvalue':
      return <KeyValueBlock block={block} />;
    case 'list':
      return <ListBlock block={block} />;
    case 'json':
      return <JsonBlock block={block} />;
    case 'markdown':
      return <ChatMarkdown text={block.text} />;
    case 'text':
      return <p className="sapres-text">{block.text}</p>;
    case 'empty':
    default:
      return <p className="sapres-muted">No data returned.</p>;
  }
};

const SapResult = ({ blocks }) => (
  <div className="sapres">
    {(blocks || []).map((b, i) => (
      <div className="sapres-block" key={i}>
        <Block block={b} />
      </div>
    ))}
  </div>
);

export default SapResult;
